// frontend/src/components/JobCostLedgerAccordion.jsx
// Project: Vision ANPR - Job Cost Ledger (Stage 4C)
// Author: Castle Traffic Ltd
// Created: 2026-10-28 13:44 GMT
// Updated: 2026-11-04 15:23 GMT
// Description: Standalone Job Edit accordion for admin-gated manual ledger entries.
// Change Log:
// 2026-11-04 15:23 GMT: Add audited Edit Cost action using existing versioned PATCH; preserve state-change, void and audit flows.
// 2026-10-28 13:44 GMT: Initial manual entry, recognition amendment, totals, audit and void UI.
// Notes:
// - Uses the canonical Stage 4B endpoints, with no changes to Job Edit save behaviour.
// - Does not merge resource Expenses with ledger totals; avoids double counting.
// - Rate selection, authority calculations, and financial margin UI are future work packages.

import React, { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";

const blankCost = () => ({
  cost_category: "", description: "", recognition_state: "estimated", quantity: "1",
  unit_label: "each", charge_basis: "fixed", unit_cost: "", currency_code: "GBP",
  tax_rate_percent: "", effective_date: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`,
  source_reference: "", evidence_reference: "", job_work_package_id: "",
});
const errorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : detail ? JSON.stringify(detail) : error?.message || "Request failed";
};
const money = (value, currency = "GBP") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(n); }
  catch { return `${n.toFixed(2)} ${currency}`; }
};
const dateOnly = (value) => value ? String(value).slice(0, 10) : "";
const parseRows = (data) => Array.isArray(data) ? data : [];

export default function JobCostLedgerAccordion({ jobId, open = true, workPackages = [] }) {
  const formRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState(blankCost);
  const [showForm, setShowForm] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [editReason, setEditReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [amending, setAmending] = useState(null);
  const [amendState, setAmendState] = useState("estimated");
  const [amendReason, setAmendReason] = useState("");
  const [eventsFor, setEventsFor] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [voidFor, setVoidFor] = useState(null);
  const [voidReason, setVoidReason] = useState("");

  const base = `/api/jobs/${jobId}/cost-ledger`;
  const refresh = useCallback(async () => {
    if (!open || !jobId) return;
    setLoading(true); setError("");
    try {
      const [list, totals] = await Promise.all([
        axiosInstance.get(`/api/jobs/${jobId}/cost-ledger`),
        axiosInstance.get(`/api/jobs/${jobId}/cost-ledger/totals`),
      ]);
      setRows(parseRows(list.data));
      setBuckets(parseRows(totals.data?.recognition_buckets));
    } catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, [jobId, open]);

  useEffect(() => {
    if (!open || !jobId) {
      setExpanded(false); setRows([]); setBuckets([]); setShowForm(false);
      setEventsFor(null); setAmending(null); setVoidFor(null);
      setEditingCost(null); setEditReason("");
      return;
    }
    if (expanded) refresh();
  }, [expanded, open, jobId, refresh]);

  useEffect(() => {
    if (showForm && editingCost) formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [showForm, editingCost]);

  const change = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const cancelForm = () => {
    setShowForm(false);
    setEditingCost(null);
    setEditReason("");
    setForm(blankCost());
  };
  const beginCreate = () => {
    if (showForm && !editingCost) { cancelForm(); return; }
    setEditingCost(null);
    setEditReason("");
    setAmending(null);
    setVoidFor(null);
    setError("");
    setForm(blankCost());
    setShowForm(true);
  };
  const beginEdit = (row) => {
    setAmending(null);
    setVoidFor(null);
    setError("");
    setMessage("");
    setEditingCost({ id: row.id, version: row.version, job_task_id: row.job_task_id });
    setEditReason("");
    setForm({
      cost_category: row.cost_category ?? "",
      description: row.description ?? "",
      recognition_state: row.recognition_state ?? "estimated",
      quantity: String(row.quantity ?? "1"),
      unit_label: row.unit_label ?? "each",
      charge_basis: row.charge_basis ?? "fixed",
      unit_cost: String(row.unit_cost ?? ""),
      currency_code: String(row.currency_code ?? "GBP").trim(),
      tax_rate_percent: row.tax_rate_percent == null ? "" : String(row.tax_rate_percent),
      effective_date: dateOnly(row.effective_date),
      source_reference: row.source_reference ?? "",
      evidence_reference: row.evidence_reference ?? "",
      job_work_package_id: row.job_work_package_id == null ? "" : String(row.job_work_package_id),
    });
    setShowForm(true);
  };
  const saveCost = async (e) => {
    e.preventDefault();
    if (!jobId || saving) return;
    setSaving(true); setError(""); setMessage("");
    try {
      const payload = {
        cost_category: form.cost_category.trim(), description: form.description.trim(),
        quantity: Number(form.quantity), unit_label: form.unit_label.trim(),
        charge_basis: form.charge_basis, unit_cost: Number(form.unit_cost),
        tax_rate_percent: form.tax_rate_percent === "" ? null : Number(form.tax_rate_percent),
        effective_date: form.effective_date,
        source_reference: form.source_reference.trim() || null,
        evidence_reference: form.evidence_reference.trim() || null,
        job_work_package_id: form.job_work_package_id ? Number(form.job_work_package_id) : null,
      };
      if (!payload.cost_category || !payload.description || !payload.unit_label || !payload.effective_date ||
          form.quantity.trim() === "" || form.unit_cost.trim() === "" ||
          !Number.isFinite(payload.quantity) || payload.quantity <= 0 ||
          !Number.isFinite(payload.unit_cost) || payload.unit_cost < 0 ||
          (payload.tax_rate_percent !== null && (!Number.isFinite(payload.tax_rate_percent) || payload.tax_rate_percent < 0 || payload.tax_rate_percent > 100))) {
        throw new Error("Enter a category, description, valid date, positive quantity and non-negative cost/tax.");
      }
      if (editingCost) {
        const reason = editReason.trim();
        if (reason.length < 3) throw new Error("Amendment reason must contain at least 3 characters.");
        await axiosInstance.patch(`${base}/${editingCost.id}`, {
          ...payload,
          expected_version: editingCost.version,
          reason,
        });
        setMessage("Cost amended successfully; audit history updated.");
        if (eventsFor === editingCost.id) await loadEvents(editingCost.id);
      } else {
        await axiosInstance.post(base, {
          ...payload,
          recognition_state: form.recognition_state,
          currency_code: form.currency_code,
        });
        setMessage("Cost recorded successfully.");
      }
      cancelForm();
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
      if (err?.response?.status === 409) await refresh();
    } finally { setSaving(false); }
  };
  const amend = async (row) => {
    if (!amendReason.trim() || amendReason.trim().length < 3) { setError("Amendment reason must contain at least 3 characters."); return; }
    setSaving(true); setError(""); setMessage("");
    try {
      await axiosInstance.patch(`${base}/${row.id}`, {
        expected_version: row.version, recognition_state: amendState, reason: amendReason.trim(),
      });
      setAmending(null); setAmendReason(""); setMessage("Recognition state updated and audited.");
      await refresh();
      if (eventsFor === row.id) await loadEvents(row.id);
    } catch (err) { setError(errorMessage(err)); await refresh(); }
    finally { setSaving(false); }
  };
  const voidEntry = async (row) => {
    if (voidReason.trim().length < 3) { setError("Void reason must contain at least 3 characters."); return; }
    setSaving(true); setError(""); setMessage("");
    try {
      await axiosInstance.post(`${base}/${row.id}/void`, {
        expected_version: row.version, reason: voidReason.trim(),
      });
      setVoidFor(null); setVoidReason(""); setMessage("Cost voided; audit history retained.");
      await refresh();
      if (eventsFor === row.id) await loadEvents(row.id);
    } catch (err) { setError(errorMessage(err)); await refresh(); }
    finally { setSaving(false); }
  };
  const loadEvents = async (entryId) => {
    setEventsFor(entryId); setEventsLoading(true); setError("");
    try { const response = await axiosInstance.get(`${base}/${entryId}/events`); setEvents(parseRows(response.data)); }
    catch (err) { setError(errorMessage(err)); setEvents([]); }
    finally { setEventsLoading(false); }
  };
  const fieldClass = "w-full min-h-10 border border-gray-300 rounded px-3 py-2 text-sm bg-white";
  const labelClass = "block text-xs text-gray-600 mb-1";
  const buttonClass = "min-h-10 rounded px-3 py-2 text-sm font-semibold text-white bg-btnGeneralNormal hover:bg-btnGeneralHover disabled:opacity-50";

  return (
    <div className="mt-6 rounded-xl border border-gray-200 overflow-hidden">
      <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold bg-btnGeneralNormal text-white"
        aria-expanded={expanded} aria-controls="job-cost-ledger-panel" onClick={() => setExpanded((value) => !value)}>
        <span>Job Cost Ledger</span><span className="ml-2">{expanded ? "▾" : "▸"}</span>
      </button>
      {expanded && <div id="job-cost-ledger-panel" className="border-t border-gray-200 p-4 space-y-4 bg-white">
        <div className="flex flex-wrap justify-between items-center gap-2">
          <div className="text-xs text-gray-600">Manual job costs; Expenses remain separately managed and are not included here.</div>
          <div className="flex gap-2">
            <button type="button" className={buttonClass} onClick={refresh} disabled={loading || saving}>{loading ? "Loading…" : "Refresh"}</button>
            <button type="button" className="min-h-10 rounded px-3 py-2 text-sm font-semibold text-white bg-btnActionNormal hover:bg-btnActionHover disabled:opacity-50"
              disabled={saving} onClick={beginCreate}>{showForm && !editingCost ? "Cancel Add Cost" : "Add Cost"}</button>
          </div>
        </div>
        {error && <div role="alert" className="text-sm text-red-700">{error}</div>}
        {message && <div role="status" className="text-sm text-green-700">{message}</div>}
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="text-sm font-semibold mb-2">Recognition totals (ex VAT)</div>
          {buckets.length ? <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {buckets.map((b) => <div key={`${b.currency_code}-${b.recognition_state}`} className="bg-gray-50 rounded p-3">
              <div className="text-xs uppercase text-gray-600">{b.recognition_state} · {b.entry_count} entries</div>
              <div className="font-semibold">{money(b.amount_ex_tax, String(b.currency_code).trim())}</div>
              <div className="text-xs text-gray-500">Known VAT: {money(b.tax_on_known_rates, String(b.currency_code).trim())}</div>
            </div>)}
          </div> : <div className="text-sm text-gray-500">No active ledger costs recorded for this job.</div>}
          <div className="text-xs text-gray-500 mt-2">Estimated, committed and actual are mutually exclusive states. Do not add these buckets together as separate liabilities.</div>
        </div>
        {showForm && <form ref={formRef} onSubmit={saveCost} className="rounded-lg border border-gray-200 p-4 space-y-3">
          <div className="text-sm font-semibold">{editingCost ? `Edit manual job cost #${editingCost.id} (v${editingCost.version})` : "Add manual job cost"}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className={labelClass}>Cost category *</label><input className={fieldClass} required maxLength={100} value={form.cost_category} onChange={(e) => change("cost_category", e.target.value)} placeholder="e.g. Licence" /></div>
            <div className="md:col-span-2"><label className={labelClass}>Description *</label><input className={fieldClass} required maxLength={2000} value={form.description} onChange={(e) => change("description", e.target.value)} /></div>
            <div><label className={labelClass}>Recognition *</label><select disabled={Boolean(editingCost)} className={fieldClass} value={form.recognition_state} onChange={(e) => change("recognition_state", e.target.value)}><option value="estimated">Estimated</option><option value="committed">Committed</option><option value="actual">Actual</option></select></div>
            <div><label className={labelClass}>Effective date *</label><input type="date" required className={fieldClass} value={form.effective_date} onChange={(e) => change("effective_date", e.target.value)} /></div>
            <div><label className={labelClass}>Work package (optional)</label><select disabled={Boolean(editingCost?.job_task_id)} className={fieldClass} value={form.job_work_package_id} onChange={(e) => change("job_work_package_id", e.target.value)}><option value="">Whole job / unallocated</option>{workPackages.map((p) => <option key={p.id} value={p.id}>{p.title || p.package_ref || `Package ${p.id}`}</option>)}</select></div>
            <div><label className={labelClass}>Charge basis *</label><select className={fieldClass} value={form.charge_basis} onChange={(e) => change("charge_basis", e.target.value)}><option value="fixed">Fixed</option><option value="quantity">Quantity</option><option value="hour">Hour</option><option value="day">Day</option><option value="other">Other</option></select></div>
            <div><label className={labelClass}>Quantity *</label><input type="number" min="0.0001" step="any" required className={fieldClass} value={form.quantity} onChange={(e) => change("quantity", e.target.value)} /></div>
            <div><label className={labelClass}>Unit label *</label><input required className={fieldClass} maxLength={80} value={form.unit_label} onChange={(e) => change("unit_label", e.target.value)} /></div>
            <div><label className={labelClass}>Unit cost (ex VAT) *</label><input type="number" min="0" step="any" required className={fieldClass} value={form.unit_cost} onChange={(e) => change("unit_cost", e.target.value)} /></div>
            <div><label className={labelClass}>VAT % (blank = unknown)</label><input type="number" min="0" max="100" step="any" className={fieldClass} value={form.tax_rate_percent} onChange={(e) => change("tax_rate_percent", e.target.value)} /></div>
            <div><label className={labelClass}>Currency</label><input disabled={Boolean(editingCost)} maxLength={3} pattern="[A-Z]{3}" required className={fieldClass} value={form.currency_code} onChange={(e) => change("currency_code", e.target.value.toUpperCase())} /></div>
            <div><label className={labelClass}>Source reference</label><input className={fieldClass} value={form.source_reference} onChange={(e) => change("source_reference", e.target.value)} /></div>
            <div className="md:col-span-2"><label className={labelClass}>Evidence reference</label><input className={fieldClass} value={form.evidence_reference} onChange={(e) => change("evidence_reference", e.target.value)} /></div>
          </div>
          {editingCost && <>
            <div className="text-xs text-gray-600">Currency and recognition are retained. Use Change state for recognition amendments. {editingCost.job_task_id ? "Work package is locked because this cost is linked to a task." : ""}</div>
            <div><label className={labelClass} htmlFor="job-cost-edit-reason">Amendment reason *</label><input id="job-cost-edit-reason" required minLength={3} maxLength={2000} className={fieldClass} value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="Explain why this cost is being corrected" /></div>
          </>}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" disabled={saving} className={buttonClass} onClick={cancelForm}>Cancel</button>
            <button type="submit" disabled={saving} className="min-h-10 rounded px-4 py-2 text-sm font-semibold bg-btnActionNormal hover:bg-btnActionHover text-white disabled:opacity-50">{saving ? "Saving…" : editingCost ? "Save Cost Amendment" : "Record Cost"}</button>
          </div>
        </form>}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm text-left text-gray-700 min-w-[780px]">
            <thead className="bg-gray-50"><tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Category / description</th><th className="px-3 py-2">Basis</th><th className="px-3 py-2">Cost ex VAT</th><th className="px-3 py-2">State</th><th className="px-3 py-2">Actions</th></tr></thead>
            <tbody>{rows.length === 0 ? <tr><td className="px-3 py-3 text-gray-500" colSpan={6}>{loading ? "Loading…" : "No ledger entries."}</td></tr> : rows.map((row) => <React.Fragment key={row.id}>
              <tr className="border-t border-gray-200 odd:bg-gray-50">
                <td className="px-3 py-2">{dateOnly(row.effective_date)}</td>
                <td className="px-3 py-2"><div className="font-semibold">{row.cost_category}</div><div>{row.description}</div><div className="text-xs text-gray-500">ID {row.id} · v{row.version}{row.source_reference ? ` · ${row.source_reference}` : ""}</div></td>
                <td className="px-3 py-2">{row.quantity} {row.unit_label} · {row.charge_basis}</td>
                <td className="px-3 py-2 font-medium">{money(Number(row.quantity) * Number(row.unit_cost), String(row.currency_code).trim())}</td>
                <td className="px-3 py-2">{row.entry_status === "voided" ? "Voided" : row.recognition_state}</td>
                <td className="px-3 py-2"><div className="flex flex-wrap gap-2">
                  <button className={buttonClass} type="button" disabled={saving} onClick={() => eventsFor === row.id ? setEventsFor(null) : loadEvents(row.id)}>Audit</button>
                  {row.entry_status === "active" && <><button type="button" className={buttonClass} disabled={saving} onClick={() => beginEdit(row)}>Edit Cost</button><button type="button" className={buttonClass} disabled={saving} onClick={() => { cancelForm(); setVoidFor(null); setAmending(row.id); setAmendState(row.recognition_state); setAmendReason(""); }}>Change state</button>
                  <button type="button" className={buttonClass} disabled={saving} onClick={() => { cancelForm(); setAmending(null); setVoidFor(row.id); setVoidReason(""); }}>Void</button></>}
                </div></td>
              </tr>
              {amending === row.id && <tr><td colSpan={6} className="p-3 border-t bg-gray-50"><div className="flex flex-wrap gap-2 items-end"><label className="text-xs">New recognition state<select className={fieldClass} value={amendState} onChange={(e) => setAmendState(e.target.value)}><option value="estimated">Estimated</option><option value="committed">Committed</option><option value="actual">Actual</option></select></label><label className="text-xs flex-1">Reason (required)<input className={fieldClass} value={amendReason} onChange={(e) => setAmendReason(e.target.value)} maxLength={2000} /></label><button type="button" className={buttonClass} disabled={saving || amendState === row.recognition_state} onClick={() => amend(row)}>Confirm change</button><button type="button" className={buttonClass} onClick={() => setAmending(null)}>Cancel</button></div></td></tr>}
              {voidFor === row.id && <tr><td colSpan={6} className="p-3 border-t bg-gray-50"><div className="flex flex-wrap gap-2 items-end"><label className="text-xs flex-1">Reason for voiding (required)<input className={fieldClass} value={voidReason} maxLength={2000} onChange={(e) => setVoidReason(e.target.value)} /></label><button type="button" className={buttonClass} disabled={saving} onClick={() => voidEntry(row)}>Confirm void</button><button type="button" className={buttonClass} onClick={() => setVoidFor(null)}>Cancel</button></div></td></tr>}
              {eventsFor === row.id && <tr><td colSpan={6} className="p-3 border-t bg-white"><div className="text-xs font-semibold mb-2">Immutable audit history — entry {row.id}</div>{eventsLoading ? "Loading…" : events.length ? <div className="space-y-2">{events.map((event) => <div key={event.id} className="text-xs border-b pb-2"><strong>{event.event_type}</strong> · {event.occurred_at ? new Date(event.occurred_at).toLocaleString("en-GB") : ""} · Actor {event.actor_user_id ?? "—"}{event.reason ? ` · ${event.reason}` : ""}</div>)}</div> : <div className="text-xs text-gray-500">No events returned.</div>}</td></tr>}
            </React.Fragment>)}</tbody>
          </table>
        </div>
      </div>}
    </div>
  );
}
